/**
 * 节点执行状态 Hook
 *
 * 提供统一的节点执行状态获取逻辑
 */

import { useMemo } from 'react';
import { useExecutionStore } from '../store/workflowStore';
import { NodeExecutionStatus } from '../types/workflow';

export interface UseNodeExecutionStatusResult {
  /** 节点执行状态 */
  executionStatus: NodeExecutionStatus;
  /** 是否当前执行节点 */
  isCurrentNode: boolean;
  /** 是否正在运行 */
  isRunning: boolean;
  /** 是否已完成 */
  isCompleted: boolean;
  /** 是否失败 */
  isFailed: boolean;
  /** 是否等待中 */
  isPending: boolean;
  /** 是否跳过 */
  isSkipped: boolean;
  /** 节点执行日志 */
  nodeLog: ReturnType<typeof useExecutionStore.getState>['nodeLogs'] extends Map<string, infer T> ? T | undefined : never;
}

/**
 * 获取节点执行状态
 *
 * @param nodeId 节点 ID
 * @returns 节点执行状态信息
 */
export function useNodeExecutionStatus(nodeId: string): UseNodeExecutionStatusResult {
  // 从 executionStore 获取状态
  const nodeLog = useExecutionStore((state) => state.nodeLogs.get(nodeId));
  const currentNodeId = useExecutionStore((state) => state.currentNodeId);

  return useMemo(() => {
    const executionStatus: NodeExecutionStatus = nodeLog?.status || NodeExecutionStatus.PENDING;
    const isCurrentNode = currentNodeId === nodeId;
    const isRunning = isCurrentNode && executionStatus === NodeExecutionStatus.RUNNING;

    return {
      executionStatus,
      isCurrentNode,
      isRunning,
      isCompleted: executionStatus === NodeExecutionStatus.COMPLETED,
      isFailed: executionStatus === NodeExecutionStatus.FAILED,
      isPending: executionStatus === NodeExecutionStatus.PENDING,
      isSkipped: executionStatus === NodeExecutionStatus.SKIPPED,
      nodeLog,
    };
  }, [nodeId, nodeLog, currentNodeId]);
}

export default useNodeExecutionStatus;
